import { FC, useMemo, useState } from "react"
import { LngLat, LngLatBounds } from "ymaps3"
import { useYandexReactComponents } from "../../../common/yandex/provider/YandexMapProvider"
import { PopupCardEvent } from "./PopupCard/PopupCardEvent"
import { ContainerPopupCardEvent } from "./ContainerPopupCardEvent"
import { z } from "zod"
import { schema } from "../schema"
import { clusterPoints, MAX_CLUSTER_ZOOM, Cluster } from "./clustering"
import style from "../style.module.css"

type CustomMarkerModels = z.infer<typeof schema>

export type CustomMarkerWithPopupProps = {
  events: CustomMarkerModels[]
  zoom: number
  bounds?: LngLatBounds
  // Приблизить карту к точке (клик по кластеру)
  onZoomTo: (center: LngLat, zoom: number) => void
}

export const CustomMarkerWithPopup: FC<CustomMarkerWithPopupProps> = ({ events, zoom, bounds, onZoomTo }) => {
  const [event, setEvent] = useState<CustomMarkerModels | undefined>()
  // Список событий по одному адресу (кластер на максимальном зуме)
  const [list, setList] = useState<Cluster<CustomMarkerModels> | undefined>()
  const components = useYandexReactComponents()

  const roundedZoom = Math.round(zoom)
  const clusters = useMemo(() => {
    const points = events.map(e => ({
      lng: e.eventAddress.event_longitude,
      lat: e.eventAddress.event_latitude,
      item: e,
    }))
    return clusterPoints(points, roundedZoom, bounds as any)
  }, [events, roundedZoom, bounds])

  if (!components) return null
  const { YMapMarker } = components.yandexMap

  const openEvent = (e: CustomMarkerModels) => {
    setList(undefined)
    setEvent(e)
  }
  const close = () => {
    setEvent(undefined)
    setList(undefined)
  }

  const clickCluster = (c: Cluster<CustomMarkerModels>) => {
    if (c.items.length === 1) return openEvent(c.items[0])
    if (roundedZoom >= MAX_CLUSTER_ZOOM) {
      // Дальше приближать бессмысленно — все события по одному адресу, показываем списком
      setEvent(undefined)
      setList(c)
      return
    }
    onZoomTo([c.lng, c.lat], Math.min(roundedZoom + 2, MAX_CLUSTER_ZOOM))
  }

  return (
    <>
      {clusters.map(c => (
        <YMapMarker key={c.key} coordinates={[c.lng, c.lat]}>
          {c.items.length === 1 ? (
            <div className={style.marker} onClick={() => clickCluster(c)} />
          ) : (
            <div
              className={style.cluster}
              style={{ ["--size" as any]: `${clusterSize(c.items.length)}px` }}
              onClick={() => clickCluster(c)}
              title={`${c.items.length} событий`}
            >
              {c.items.length > 999 ? "999+" : c.items.length}
            </div>
          )}
        </YMapMarker>
      ))}

      <ContainerPopupCardEvent location={pickLocation(event)} isVisible={!!event}>
        <PopupCardEvent onClose={close} model={event} />
      </ContainerPopupCardEvent>

      <ContainerPopupCardEvent location={list ? [list.lng, list.lat] : undefined} isVisible={!!list}>
        {list && (
          <div className={style.clusterList} onClick={e => e.stopPropagation()}>
            <div className={style.clusterListHeader}>
              <span>{list.items.length} событий здесь</span>
              <button onClick={close} aria-label="Закрыть">✕</button>
            </div>
            <div className={style.clusterListBody}>
              {list.items.map(e => (
                <button key={e.eventId} className={style.clusterListItem} onClick={() => openEvent(e)}>
                  {e.eventName}
                </button>
              ))}
            </div>
          </div>
        )}
      </ContainerPopupCardEvent>
    </>
  )
}

// Размер кружка растёт с количеством, но не бесконечно
const clusterSize = (n: number) => Math.round(Math.min(64, 32 + Math.log10(n) * 14))

const pickLocation = (dto: CustomMarkerModels | undefined): LngLat | undefined => {
  if (!dto?.eventAddress) return undefined
  return [dto.eventAddress.event_longitude, dto.eventAddress.event_latitude]
}
